// Agent API module for all /v1/agent/* endpoints.

import { fetchWithAuthRetry, getBaseUrl } from './client'
import { useAuthStore } from '@/stores/useAuthStore'
import type { ContextRef } from '@/lib/askAbout'
import type { ReviewQueueResponse, AgentApprovalResponse, AttachmentRef } from '@/types'

const AGENT_BASE = '/agent'

/** Build full URL for agent endpoints. */
function agentUrl(path: string): string {
  return `${getBaseUrl()}${AGENT_BASE}${path}`
}

/** Build common headers for the given attempt's token plus business context. */
function authHeaders(token: string | null): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  const user = useAuthStore.getState().user
  const businessId = (user as Record<string, unknown> | null)?.default_business_id as
    | string
    | undefined
  if (businessId) {
    headers['X-Business-ID'] = businessId
  }
  return headers
}

/**
 * Turn a failed response into an `AgentApiError` carrying a message a person
 * can read.
 *
 * The backend's `detail` is written for humans; the HTTP status is not — and it
 * used to be the fallback, so a redundant POST wrote `Failed to answer
 * question: 400` straight into the user's transcript (#2246). The status is
 * still carried on the error object, which is where callers branch on it; it
 * never becomes part of the message. `detail` is only used when it is genuinely
 * a string: FastAPI's validation errors put an array of objects there, which
 * would otherwise render as `[object Object]`.
 */
async function failure(response: Response, fallback: string): Promise<AgentApiError> {
  const body = (await response.json().catch(() => ({}))) as {
    detail?: unknown
    message?: unknown
  }
  const detail = typeof body.detail === 'string' ? body.detail.trim() : ''
  const message = typeof body.message === 'string' ? body.message.trim() : ''
  return new AgentApiError(detail || message || fallback, response.status)
}

/** Ack from POST /v1/agent/chat (#2569 Part 1) — the message was recorded and
 * the business's Hermes agent was woken. NOT the assistant's reply: that
 * arrives later over GET /events/stream as an `agent_run_completed` /
 * `agent_run_failed` Notification with `payload.channel === "in_app"`. */
export interface AgentChatSendResponse {
  conversation_id: string
  message_id: string
  created_at: string
  /** The assistant is still being set up (first message on a new account). */
  assistant_setting_up: boolean
  /** Seconds until the assistant can take it (it is starting up); null when it answers as usual. */
  assistant_starting_seconds: number | null
}

/** POST /v1/agent/chat -- send a message to the business's Hermes agent over
 * the in-app channel. Fire-and-forget by design; see AgentChatSendResponse. */
export async function sendChatMessage(
  message: string,
  attachments?: AttachmentRef[],
  contextRefs?: ContextRef[]
): Promise<AgentChatSendResponse> {
  const response = await fetchWithAuthRetry((token) =>
    fetch(agentUrl('/chat'), {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({
        message,
        attachments: attachments ?? [],
        context_refs: contextRefs ?? [],
      }),
    })
  )

  if (!response.ok) {
    throw await failure(response, 'Could not send your message. Please try again.')
  }

  return response.json()
}

/** One message on the business's single in-app Hermes conversation. */
export interface AgentChatHistoryMessage {
  id: string
  direction: 'inbound' | 'outbound'
  sender_label: string | null
  content: string
  created_at: string
  /** Set when the message is a request an outside platform sent. */
  platform_request?: { id: string; platform: string } | null
  /** Set when the message is the card of work handed to a team member. */
  production_brief?: { id: string; kind: string } | null
  /** A team member's post (its name is `sender_label`) or the owner's answer to it. */
  agent_role_id?: string | null
  production_brief_id?: string | null
}

/** A platform the owner can filter the thread by. */
export interface PlatformSource {
  id: string
  label: string
}

export interface AgentChatHistoryResponse {
  /** Null when nothing has been sent yet on this business. */
  conversation_id: string | null
  /** Oldest first. */
  messages: AgentChatHistoryMessage[]
  platform_sources?: PlatformSource[]
}

/** GET /v1/agent/chat/history -- the one in-app conversation's recent
 * messages. `source` is 'me' (without platform requests) or a platform id.
 * There is no session/thread picker for this channel (#2569 Part 1). */
export async function getChatHistory(
  limit = 50,
  source?: string
): Promise<AgentChatHistoryResponse> {
  const params = new URLSearchParams({ limit: String(limit) })
  if (source) params.set('source', source)
  const response = await fetchWithAuthRetry((token) =>
    fetch(agentUrl(`/chat/history?${params}`), {
      method: 'GET',
      headers: authHeaders(token),
    })
  )

  if (!response.ok) {
    throw await failure(response, 'Could not load this conversation.')
  }

  return response.json()
}

/** POST /v1/agent/app-open -- the app was opened or came back: wake the business's agent. */
export async function appOpened(): Promise<void> {
  await fetchWithAuthRetry((token) =>
    fetch(agentUrl('/app-open'), { method: 'POST', headers: authHeaders(token) })
  )
}

/** POST /v1/agent/chat/ready -- the chat is open: start what hands messages to the agent. */
export async function readyChat(): Promise<void> {
  await fetchWithAuthRetry((token) =>
    fetch(agentUrl('/chat/ready'), { method: 'POST', headers: authHeaders(token) })
  )
}

/** One day of the owner's chat before its last New chat. */
export interface PastChat {
  day: string
  title: string
  message_count: number
  last_message_at: string
}

function pastQuery(roleId?: string): string {
  const params = new URLSearchParams({ tz: Intl.DateTimeFormat().resolvedOptions().timeZone })
  if (roleId) params.set('role_id', roleId)
  return params.toString()
}

/** GET /v1/agent/chat/past -- the chat before its last New chat, one entry per day. */
export async function listPastChats(roleId?: string): Promise<PastChat[]> {
  const response = await fetchWithAuthRetry((token) =>
    fetch(agentUrl(`/chat/past?${pastQuery(roleId)}`), { headers: authHeaders(token) })
  )
  if (!response.ok) throw await failure(response, 'Could not load past chats.')
  return ((await response.json()) as { chats: PastChat[] }).chats
}

/** GET /v1/agent/chat/past/:day -- every message of one past day, oldest first. */
export async function getPastChat(
  day: string,
  roleId?: string
): Promise<AgentChatHistoryMessage[]> {
  const response = await fetchWithAuthRetry((token) =>
    fetch(agentUrl(`/chat/past/${day}?${pastQuery(roleId)}`), { headers: authHeaders(token) })
  )
  if (!response.ok) throw await failure(response, 'Could not load this chat.')
  return ((await response.json()) as { messages: AgentChatHistoryMessage[] }).messages
}

/** Start a new chat with Marketing: the thread then shows only what comes after, and what
 * came before stays readable in Past chats. */
export async function clearChatHistory(): Promise<{ cleared_at: string }> {
  const response = await fetchWithAuthRetry((token) =>
    fetch(agentUrl('/chat/clear'), { method: 'POST', headers: authHeaders(token) })
  )
  if (!response.ok) {
    throw await failure(response, 'Could not clear this chat.')
  }
  return response.json()
}

/** GET /v1/agent/approvals — `chat` ('marketing' or a team member's id) keeps that chat's own. */
export async function getApprovals(
  limit = 50,
  offset = 0,
  chat?: string
): Promise<ReviewQueueResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  })
  if (chat) params.set('chat', chat)
  const response = await fetchWithAuthRetry((token) =>
    fetch(agentUrl(`/approvals?${params}`), {
      method: 'GET',
      headers: authHeaders(token),
    })
  )

  if (!response.ok) {
    throw await failure(response, 'Could not load pending approvals.')
  }

  return response.json()
}

/** POST /v1/agent/approvals/:id/approve; `always` lets that kind of paid job run without asking. */
export async function approveAction(
  approvalId: string,
  always = false
): Promise<AgentApprovalResponse> {
  const response = await fetchWithAuthRetry((token) =>
    fetch(agentUrl(`/approvals/${approvalId}/approve`), {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ always }),
    })
  )

  if (!response.ok) {
    throw await failure(response, 'Could not approve this action.')
  }

  return response.json()
}

/** POST /v1/agent/approvals/:id/reject */
export async function rejectAction(
  approvalId: string,
  reason: string
): Promise<AgentApprovalResponse> {
  const response = await fetchWithAuthRetry((token) =>
    fetch(agentUrl(`/approvals/${approvalId}/reject`), {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ reason }),
    })
  )

  if (!response.ok) {
    throw await failure(response, 'Could not reject this action.')
  }

  return response.json()
}

/** POST /v1/agent/approvals/:id/retry — runs a failed approved action again. */
export async function retryAction(approvalId: string): Promise<AgentApprovalResponse> {
  const response = await fetchWithAuthRetry((token) =>
    fetch(agentUrl(`/approvals/${approvalId}/retry`), {
      method: 'POST',
      headers: authHeaders(token),
    })
  )

  if (!response.ok) {
    throw await failure(response, 'Could not retry this action.')
  }

  return response.json()
}

/** Custom error class for agent API errors. */
export class AgentApiError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'AgentApiError'
    this.status = status
  }
}
